import { VaultUnlockModal } from "@/components/tools/vault/unlock-modal";
import { Button } from "@/components/ui/button";
import { VaultService } from "@/services/vault";
import { Copy, Eye, EyeOff, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const AUTO_HIDE_MS = 45_000;

export interface VaultPasswordCardProps {
    credentialId: number;
    username?: string | null;
}

export function VaultPasswordCard({ credentialId, username }: VaultPasswordCardProps) {
    const [unlocked, setUnlocked] = useState(false);
    const [unlockOpen, setUnlockOpen] = useState(false);
    const [password, setPassword] = useState<string | null>(null);

    useEffect(() => {
        VaultService.getSession()
            .then(session => {
                setUnlocked(session.unlocked);
                if (!session.unlocked) VaultService.setToken(null);
            })
            .catch(() => setUnlocked(false));
    }, []);

    // The secret never stays on screen longer than the auto-hide window.
    useEffect(() => {
        if (!password) return;
        const timeout = window.setTimeout(() => setPassword(null), AUTO_HIDE_MS);
        return () => window.clearTimeout(timeout);
    }, [password]);

    const reveal = async (): Promise<string | null> => {
        try {
            const res = await VaultService.reveal(credentialId);
            setPassword(res.password);
            setUnlocked(true);
            return res.password;
        } catch (e: any) {
            if (e?.json?.code === "vault_locked") {
                setUnlocked(false);
                VaultService.setToken(null);
                setUnlockOpen(true);
                return null;
            }
            toast.error(e?.json?.detail || "Failed to reveal password");
            return null;
        }
    };

    const copyUsername = async () => {
        if (!username) return;
        try {
            await navigator.clipboard.writeText(username);
            toast.success("Username copied");
        } catch {
            toast.error("Clipboard unavailable");
        }
    };

    const copyPassword = async () => {
        const value = password ?? (await reveal());
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            await VaultService.copy(credentialId);
            toast.success("Password copied");
        } catch {
            toast.error("Clipboard unavailable");
        }
    };

    const lockVault = async () => {
        await VaultService.lock();
        setUnlocked(false);
        setPassword(null);
        toast.success("Vault locked");
    };

    return (
        <>
            <div className="mt-2 flex w-full flex-col items-start space-y-3 rounded-md bg-white p-4 shadow-md">
                <div className="flex w-full items-center justify-between">
                    <h3 className="text-sm font-semibold">Login</h3>
                    {unlocked && (
                        <button type="button" onClick={lockVault} className="flex items-center gap-1 text-xs text-muted-foreground hover:underline">
                            <Lock className="h-3 w-3" />
                            Lock vault
                        </button>
                    )}
                </div>

                {username && (
                    <div className="w-full">
                        <div className="text-xs uppercase text-muted-foreground">Username</div>
                        <div className="mt-1 flex items-center gap-2">
                            <code className="flex-1 select-all break-all rounded bg-muted px-3 py-2 font-mono text-sm">{username}</code>
                            <Button variant="outline" size="icon" title="Copy" onClick={copyUsername}>
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

                <div className="w-full">
                    <div className="text-xs uppercase text-muted-foreground">Password</div>
                    <div className="mt-1 flex items-center gap-2">
                        <code className="flex-1 select-all break-all rounded bg-muted px-3 py-2 font-mono text-sm">
                            {password ?? "••••••••••••"}
                        </code>
                        <Button
                            variant="outline"
                            size="icon"
                            title={password ? "Hide" : "Reveal"}
                            onClick={() => (password ? setPassword(null) : unlocked ? reveal() : setUnlockOpen(true))}
                        >
                            {password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button variant="outline" size="icon" title="Copy" onClick={copyPassword}>
                            <Copy className="h-4 w-4" />
                        </Button>
                    </div>
                    {!unlocked && <p className="mt-2 text-xs text-muted-foreground">Unlock the vault with your TOTP code to access it.</p>}
                </div>
            </div>

            <VaultUnlockModal open={unlockOpen} onOpenChange={setUnlockOpen} onUnlocked={() => reveal()} />
        </>
    );
}
